'use client';

import { useAuth } from '@/context/Auth';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import LoadingSpinner from '@/components/ui/spinner';

export function GuestOnly({ children, isBusiness }: { children: React.ReactNode, isBusiness?: boolean }) {
  const { idTokenPayload, loading, isAccessResolved, isPermanentlyBanned } =
    useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Check if we're in the middle of an OAuth callback (has code param)
  const isOAuthCallback = typeof window !== 'undefined' &&
    (searchParams.get('code') !== null || searchParams.get('state') !== null);

  useEffect(() => {
    // Only send the user into the app once we've CONFIRMED access: authenticated,
    // the profile check has settled, and they're not permanently banned. Banned
    // users are handled by the Auth provider (sign out + redirect to the
    // suspended login screen) — we must never bounce them to /home first.
    if (!loading && idTokenPayload && isAccessResolved && !isPermanentlyBanned) {
      setIsRedirecting(true);

      // Check if there's a redirect parameter. The middleware only ever sees
      // the pathname (fragments aren't sent to the server), but the browser
      // carries the original fragment over onto this /login URL per the
      // redirect spec, so window.location.hash still has it here.
      const redirectPath = searchParams.get('redirect');

      if (redirectPath && redirectPath.startsWith('/')) {
        // Redirect to the originally requested page
        router.replace(redirectPath + window.location.hash);
      } else if (isBusiness) {
        router.replace('/business');
      } else {
        // Redirect to /home for profile status check
        router.replace('/home');
      }
    }
  }, [
    idTokenPayload,
    loading,
    isAccessResolved,
    isPermanentlyBanned,
    router,
    isBusiness,
    searchParams,
  ]);

  // Hold a spinner while authenticated-but-unresolved or banned, so a
  // suspended user never sees the login form flash or the app.
  const verifyingAuthedUser =
    !!idTokenPayload && (!isAccessResolved || isPermanentlyBanned);

  // Show loading spinner during OAuth callback processing or redirect
  if (loading || isOAuthCallback || isRedirecting || verifyingAuthedUser) {
    return (
      <div className="min-h-svh flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return <>{children}</>;
}
